import moment from "moment";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ApiTaskTableRow } from "src/api/types";
import Button from "src/components/Button";
import Indicator from "src/components/layout/Indicator";
import OnlineIndicator from "src/components/OnlineIndicator";
import Skeleton from "src/components/Skeleton";
import Table from "src/components/Table";
import TaskMatchProfile from "src/components/tasks/TaskMatchProfile";
import TaskMatchProgress from "src/components/tasks/TaskMatchProgress";
import TaskStatus from "src/components/tasks/TaskStatus";
import TitleCut from "src/components/TitleCut";
import UserAvatars from "src/components/UserAvatars";
import useGet from "src/hooks/rest/useGet";
import useWindowSize from "src/hooks/useWindowSize";
import { formatPrice } from "src/lib/formatters";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useModules from "src/hooks/selectors/useModules";
import TaskTypeIndicator from "src/components/tasks/TaskTypeIndicator";
import useElementSize from "src/hooks/useElementSize";

interface TaskTableRowProps {
	task: ApiTaskTableRow;
	setModalTask?: (hid: string) => void;
	variant?: "sub-task";
}

const TaskTableRow = ({ task, setModalTask, variant }: TaskTableRowProps) => {
	const titleCell = useElementSize();
	const { t } = useTranslation("tasks", {
		keyPrefix: "task-table-row",
	});
	const { modules } = useModules();
	const { settings } = useTenant();
	const [visible, setVisible] = useState(false);
	const start = moment(task.start_date).format("DD-MM-YYYY");
	const end = task.end_date
		? moment(task.end_date).format("DD-MM-YYYY")
		: null;
	const { isTablet } = useWindowSize();
	const students = task.amount_of_students;
	const hasSubtasks = task.has_subtasks ? true : false;
	const match =
		task?.matches && students === 1 ? task.matches.users[0] : null;
	let date = `${start} - ${end}`;
	if (start === end || !end) {
		date = start;
	}
	return (
		<>
			<Table.Row
				className={`${variant === "sub-task" && "bg-gray"}`}
				key={`table-task-${task.hid}`}
			>
				<Table.Cell className="w-40">
					<div className="relative flex w-max">
						{(students > 1 || hasSubtasks) &&
						task.type !== "INTERNAL" ? (
							<div className="w-40">
								<TaskMatchProgress
									{...{ students }}
									matched={task.matches.count}
								/>
							</div>
						) : (
							<TaskStatus status={task.status} />
						)}

						{task.unread_messages > 0 && (
							<Indicator
								style={{
									position: "absolute",
									top: -8,
									right: -8,
								}}
							>
								{task.unread_messages}
							</Indicator>
						)}
					</div>
				</Table.Cell>
				<Table.Cell className="w-full">
					<div ref={titleCell.ref}></div>
					<div style={{ width: titleCell.size.width }}>
						<TitleCut
							color={variant === "sub-task" ? "gray" : "white"}
						>
							{task.title}
						</TitleCut>
					</div>
				</Table.Cell>
				<Table.Cell>
					{task.type !== "INTERNAL" && (
						<>
							{task.registrations?.users && !match && (
								<>
									{task.registrations.count > 20 ? (
										<p>
											{t("registrations", {
												count: task.registrations.count,
											})}
										</p>
									) : (
										<UserAvatars
											users={task.registrations.users}
											overlay={
												<div className="absolute inset-0 bg-primary backdrop-blur-sm rounded-md flex justify-center items-center text-white">
													<strong>
														{
															task.registrations
																.count
														}
													</strong>
												</div>
											}
										/>
									)}
								</>
							)}
							{task.registrations.count === 0 && (
								<small>{t("no-registrations")}</small>
							)}
							{match && <TaskMatchProfile user={match} />}
						</>
					)}
				</Table.Cell>
				<Table.Cell>
					{!hasSubtasks && task.type !== "INTERNAL" ? (
						<>
							{formatPrice(
								task.budget_per_hour
									? task.budget_per_hour
									: task.budget
							)}{" "}
							{task.amount_of_students > 1 &&
								!task?.budget_per_hour &&
								"p.p."}
							{task?.budget_per_hour ? t("per-hour") : null}
						</>
					) : (
						false
					)}
				</Table.Cell>
				<Table.Cell>
					{task.type !== "INTERNAL" && (
						<p className="whitespace-nowrap">{date}</p>
					)}
				</Table.Cell>
				{modules.includes("extern-task") && (
					<Table.Cell className="w-60">
						<TaskTypeIndicator type={task.type} />
					</Table.Cell>
				)}
				<Table.Cell className="w-[50px]">
					<div className="flex items-center justify-end gap-2">
						<OnlineIndicator
							state={task.is_visible ? "online" : "offline"}
						/>

						{hasSubtasks ? (
							<>
								<Button
									iconOnly
									icon={`fas fa-angle-${
										visible ? "up" : "down"
									}`}
									type="gray"
									onClick={() => setVisible(!visible)}
								/>
								<Button
									iconOnly
									icon="fas fa-retweet"
									type="gray"
									to={`/tasks/create?repeat=${task?.hid}`}
								/>

								<Button
									iconOnly
									icon="fas fa-eye"
									to={`/tasks/${task.hid}/show`}
								/>
							</>
						) : (
							<>
								{task.type === "DRAFT" && setModalTask && (
									<Button
										onClick={() => setModalTask(task.hid)}
										iconOnly
										icon="fas fa-arrow-right"
										type="primary"
									></Button>
								)}

								{task.type === "INTERNAL" && (
									<>
										<Button
											iconOnly
											icon="fas fa-pencil"
											type="gray"
											to={{
												modal: `/tasks/${task?.hid}/create`,
											}}
										/>
									</>
								)}

								{task.type === "PUBLISHED" && (
									<>
										<Button
											iconOnly
											icon="fas fa-retweet"
											type="gray"
											to={`/tasks/create?repeat=${task?.hid}`}
										/>
										<Button
											iconOnly
											icon="fas fa-eye"
											to={`/tasks/${task.hid}/show${
												task?.links?.registration &&
												!settings?.tasks
													?.budget_per_hour &&
												!isTablet
													? `/${task?.links?.registration}`
													: ""
											}`}
										/>
									</>
								)}
							</>
						)}
					</div>
				</Table.Cell>
			</Table.Row>
			{hasSubtasks && visible && <SubtaskRows id={task.id} />}
		</>
	);
};

interface SubtaskRowsProps {
	id: number | string;
}

const SubtaskRows = ({ id }: SubtaskRowsProps) => {
	const [{ data: rows }, loading] = useGet(
		`/company/tasks?sub_parent_id=${id}&variant=table&recent=1`
	);
	if (loading) {
		return (
			<Table.Row>
				<Table.Cell colSpan={6}>
					<div className="p-6 flex justify-center items-center">
						<span className="base-loader"></span>
					</div>
				</Table.Cell>
			</Table.Row>
		);
	}

	return (
		<>
			{(rows || []).length === 0 && (
				<Table.Row>
					<Table.Cell colSpan={6}>
						<div className="p-6 flex justify-center items-center">
							<strong>Geen shifts gevonden</strong>
						</div>
					</Table.Cell>
				</Table.Row>
			)}
			{(rows || []).map((item: ApiTaskTableRow) => (
				<TaskTableRow
					key={`task-${item.id}`}
					variant="sub-task"
					task={item}
				/>
			))}
		</>
	);
};

const Loading = () => {
	return (
		<Table.Row className=" ">
			<Table.Cell>
				<Skeleton className="h-6 w-full" />
			</Table.Cell>
			<Table.Cell>
				<Skeleton className="h-6 w-full" />
			</Table.Cell>
			<Table.Cell>
				<UserAvatars.Loading />
			</Table.Cell>
			<Table.Cell>
				<Skeleton className="h-6 w-full" />
			</Table.Cell>
			<Table.Cell>
				<div className="flex gap-2 max-w-[80%]">
					<Skeleton className="h-6 w-full" />
					<Skeleton className="h-6 w-full" />
				</div>
			</Table.Cell>
			<Table.Cell>
				<div className="flex gap-2">
					<Skeleton className="h-10 w-10" />
					<Skeleton className="h-10 w-10" />
				</div>
			</Table.Cell>
		</Table.Row>
	);
};

TaskTableRow.Loading = Loading;
export default TaskTableRow;
